import { createSelector } from '@reduxjs/toolkit';
import { RUN_KIND } from './stockSyncConstants';
import {
  alignmentCounts,
  channelLabel,
  filterAlignmentRows,
  isRunActive,
  locationOptions,
  previewStateOf,
  usableChannels,
} from './stockSyncUtils';

const NO_ROWS = [];

const selectChannels = (state) => state.stockSync.channels;

/** The stores there is something to sync with, from the page's warehouse. */
export const selectUsableChannels = createSelector([selectChannels], usableChannels);

export const selectChannelOptions = createSelector([selectUsableChannels], (rows) =>
  rows.map((row) => ({ value: row.channel.id, label: channelLabel(row) }))
);

/** How the store in use stands, once read. */
export const selectStockSyncDetail = (state) => state.stockSync.detail;

export const selectRunOf = (kind) => (state) => state.stockSync.detail?.runs?.[kind] ?? null;

export const selectIsRunActive = (kind) => (state) => isRunActive(selectRunOf(kind)(state));

export const selectLocationOptions = createSelector(
  [(state) => state.stockSync.locations, selectStockSyncDetail],
  (locations, detail) => locationOptions(locations, detail?.location)
);

/** Where the comparison in the store's runs stands, for the dialog: it never reads the rows. */
export const selectRunPreviewState = (state) =>
  previewStateOf(selectRunOf(RUN_KIND.STOCK_PREVIEW)(state), state.stockSync.alignment.expiredToken);

/* The comparison under review, rows and all. */

/** Where the comparison under review stands; the clock says when it expired. */
export const selectPreviewState = (state) =>
  previewStateOf(state.stockSync.alignment.preview, state.stockSync.alignment.expiredToken);

const selectPreviewRows = (state) => state.stockSync.alignment.preview?.result?.rows ?? NO_ROWS;
const selectAlignmentSearch = (state) => state.stockSync.alignment.filters.search;
const selectAlignmentState = (state) => state.stockSync.alignment.filters.state;

export const selectFilteredAlignmentRows = createSelector(
  [selectPreviewRows, selectAlignmentSearch, selectAlignmentState],
  (rows, search, rowState) => filterAlignmentRows(rows, { search, state: rowState })
);

export const selectAlignmentPageRows = createSelector(
  [
    selectFilteredAlignmentRows,
    (state) => state.stockSync.alignment.filters.page,
    (state) => state.stockSync.alignment.filters.rowsPerPage,
  ],
  (rows, page, rowsPerPage) => rows.slice((page - 1) * rowsPerPage, page * rowsPerPage)
);

export const selectAlignmentCounts = createSelector([selectPreviewRows], alignmentCounts);
